import { useMemo } from 'react';
import { addDays, isSameDay, isSameMonth, parseISO, format } from 'date-fns';
import type { AvailabilityRule, DateSpecificAvailability, PTODay } from '../hooks/useAvailability';

const DAYS_OF_WEEK_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface MonthCalendarGridProps {
  startDate: Date;
  weeksToShow: number;
  availability: AvailabilityRule[];
  dateOverrides: DateSpecificAvailability[];
  pto: PTODay[];
  currentMonth: Date;
  onSelectDate: (date: Date) => void;
}

export function MonthCalendarGrid({
  startDate,
  weeksToShow,
  availability,
  dateOverrides,
  pto,
  currentMonth,
  onSelectDate,
}: MonthCalendarGridProps) {
  const today = useMemo(() => new Date(), []);

  const days = useMemo(() => {
    const totalDays = weeksToShow * 7;
    return Array.from({ length: totalDays }, (_, i) => addDays(startDate, i));
  }, [startDate, weeksToShow]);

  const getRuleForDayOfWeek = (dayOfWeek: number): AvailabilityRule | null => {
    return availability.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;
  };

  const getOverrideForDate = (date: Date): DateSpecificAvailability | null => {
    return dateOverrides.find((o) => isSameDay(parseISO(o.date), date)) ?? null;
  };

  const getPtoForDate = (date: Date): PTODay | null => {
    return pto.find((p) => isSameDay(parseISO(p.date), date)) ?? null;
  };

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7));
    }
    return result;
  }, [days]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {DAYS_OF_WEEK_HEADERS.map((dayName, i) => (
          <div
            key={i}
            className="py-2 text-center text-xs font-medium text-gray-500"
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {weeks.map((week, weekIndex) =>
          week.map((date, dayIndex) => {
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isToday = isSameDay(date, today);
            const hasPto = getPtoForDate(date) !== null;
            const hasOverride = getOverrideForDate(date) !== null;
            const hasStandardHours = getRuleForDayOfWeek(date.getDay()) !== null;
            const isWorking = !hasPto && (hasOverride || hasStandardHours);

            // Background color
            let bgClass = 'bg-white';
            if (!isCurrentMonth) bgClass = 'bg-gray-50';
            else if (hasPto) bgClass = 'bg-amber-50';
            else if (isWorking) bgClass = 'bg-green-50';

            // Status dot color
            let dotClass = 'bg-gray-300';
            if (hasPto) dotClass = 'bg-amber-500';
            else if (isWorking) dotClass = 'bg-green-500';

            return (
              <button
                type="button"
                key={`${weekIndex}-${dayIndex}`}
                onClick={() => onSelectDate(date)}
                className={`
                  relative flex flex-col items-center justify-start p-1 min-h-[44px] w-full
                  border-r border-b border-gray-100 last:border-r-0
                  active:bg-blue-50 transition-colors
                  ${bgClass}
                  ${!isCurrentMonth ? 'opacity-40' : ''}
                `}
              >
                {/* Date number */}
                <span
                  className={`
                    text-sm leading-none
                    ${isToday
                      ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold'
                      : isCurrentMonth
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-400'
                    }
                  `}
                >
                  {format(date, 'd')}
                </span>

                {/* Status dot + override indicator */}
                <div className="mt-1 flex items-center gap-0.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
                  {hasOverride && !hasPto && (
                    <div className="w-1 h-1 rounded-full bg-blue-500" />
                  )}
                </div>

                {/* PTO label */}
                {hasPto && isCurrentMonth && (
                  <span className="text-[9px] text-amber-700 font-medium mt-0.5 leading-none">
                    PTO
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
